import React from 'react';
import { Box, Typography } from '@mui/material';
import SimpleTaxonomySelectionV3 from '../SimpleTaxonomySelectionV3';
import TaxonomyContext from '../TaxonomyContext';
import {
  UseFormSetValue,
  UseFormGetValues,
  UseFormWatch,
} from 'react-hook-form';

// Form data interface (matching RegisterAssetPage)
interface FormData {
  layer: string;
  layerName: string;
  categoryCode: string;
  categoryName: string;
  subcategoryCode: string;
  subcategoryName: string;
  subcategoryNumericCode: string;
  name: string;
  description: string;
  source: string;
  tags: string[];
  files: File[];
  hfn: string;
  mfa: string;
  sequential: string;
  trainingData?: any;
  layerSpecificData?: {
    components: any[];
  };
  albumArtUrl?: string;
  albumArtSource?: string;
  albumArtQuality?: string;
  extractedSongData?: any;
  bpm?: number;
  songMetadata?: any;
  aiMetadata?: any;
}

interface TaxonomySelectionStepProps {
  // Current selections
  selectedLayer: string;
  selectedCategoryCode: string;
  selectedSubcategoryCode: string;

  // Form methods
  setValue: UseFormSetValue<FormData>;
  getValues: UseFormGetValues<FormData>;
  watch: UseFormWatch<FormData>;

  // Callbacks
  onSubcategorySelect: (subcategoryCode: string) => Promise<void>;

  // Optional
  disabled?: boolean;
  loading?: boolean;
  error?: string;
}

/**
 * Step 2: Taxonomy Selection
 *
 * This component handles the taxonomy selection workflow including:
 * - Layer, category, and subcategory selection via SimpleTaxonomySelectionV3
 * - Debug information display (development only)
 * - Taxonomy context display when selection is complete
 * - Integration with form state management
 */
const TaxonomySelectionStep: React.FC<TaxonomySelectionStepProps> = ({
  selectedLayer,
  selectedCategoryCode,
  selectedSubcategoryCode,
  setValue,
  getValues,
  watch,
  onSubcategorySelect,
  disabled = false,
  loading = false,
  error,
}) => {
  // If there's an error from parent, display it
  if (error) {
    return (
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            p: 2,
            bgcolor: 'error.light',
            color: 'error.contrastText',
            borderRadius: 1,
            mb: 2,
          }}
        >
          {error}
        </Box>
        {/* Still show the component even with error for user to retry */}
      </Box>
    );
  }

  const content = (
    <>
      <SimpleTaxonomySelectionV3
        selectedLayer={selectedLayer}
        onLayerSelect={layer => {
          setValue('layer', layer);
          setValue('categoryCode', '');
          setValue('subcategoryCode', '');
        }}
        selectedCategoryCode={selectedCategoryCode}
        onCategorySelect={categoryCode => {
          setValue('categoryCode', categoryCode);
        }}
        selectedSubcategoryCode={selectedSubcategoryCode}
        onSubcategorySelect={subcategoryCode => {
          if (!selectedCategoryCode) return Promise.resolve();
          return onSubcategorySelect(subcategoryCode);
        }}
      />

      {/* Debug component - only shown in development with debug parameter */}
      {process.env.NODE_ENV !== 'production' &&
        window.location.search.includes('debug=true') && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="subtitle2">Debug Info:</Typography>
            <Typography variant="body2">
              Layer: {selectedLayer || 'None'}
              <br />
              Category: {selectedCategoryCode || 'None'}
              <br />
              Subcategory: {selectedSubcategoryCode || 'None'}
            </Typography>
            {selectedLayer && selectedCategoryCode && (
              <Typography variant="body2" color="primary">
                Expected subcategories should load for: {selectedLayer}.
                {selectedCategoryCode}
              </Typography>
            )}
          </Box>
        )}

      {/* Display selected taxonomy context at bottom of Step 2 */}
      {selectedLayer && selectedCategoryCode && selectedSubcategoryCode && (
        <Box sx={{ mt: 3 }}>
          <TaxonomyContext
            layer={selectedLayer}
            layerName={getValues('layerName')}
            categoryCode={selectedCategoryCode}
            categoryName={getValues('categoryName')}
            subcategoryCode={selectedSubcategoryCode}
            subcategoryName={getValues('subcategoryName')}
          />
        </Box>
      )}
    </>
  );

  // If disabled, show overlay
  if (disabled) {
    return (
      <Box sx={{ position: 'relative', opacity: 0.6, pointerEvents: 'none' }}>
        {content}
      </Box>
    );
  }

  // If loading, show loading overlay
  if (loading) {
    return (
      <Box sx={{ position: 'relative' }}>
        {content}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
        >
          <Typography>Loading taxonomy...</Typography>
        </Box>
      </Box>
    );
  }

  return content;
};

export default TaxonomySelectionStep;
